import { createRouter, createWebHistory, RouterView } from 'vue-router'

import { useAuthStore } from '@/store/auth'

const routes = [
  // Analytics routes (more specific paths, must come before catch-all)
  {
    path: '/sales',
    component: () => import('@/components/AnalyticsLayout.vue'),
    meta: { requiredGroup: 'LEADS' },
    children: [
      {
        path: '',
        component: () => import('@/components/sales/SalesLayout.vue'),
        children: [
          { path: '', name: 'sales', redirect: '/sales/funnel' },
          {
            path: 'funnel',
            name: 'sales-funnel',
            component: () => import('@/components/sales/FunnelPage.vue')
          },
          {
            path: 'owners',
            name: 'sales-owners',
            component: () => import('@/components/sales/OwnersPage.vue')
          }
        ]
      },
      {
        path: 'owners/:ownerName',
        name: 'sales-owner-detail',
        component: () => import('@/components/sales/OwnerDetailPage.vue'),
        props: true
      }
    ]
  },
  {
    path: '/onboarding',
    component: () => import('@/components/AnalyticsLayout.vue'),
    meta: { requiredGroup: 'LEADS' },
    children: [
      {
        path: '',
        name: 'onboarding',
        component: () => import('@/components/onboarding/OnboardingPage.vue')
      }
    ]
  },
  {
    path: '/churn',
    component: () => import('@/components/AnalyticsLayout.vue'),
    meta: { requiredGroups: ['LEADS', 'SALES', 'FINANCE'] },
    children: [
      {
        path: '',
        name: 'churn',
        component: () => import('@/components/churn/ChurnPage.vue')
      }
    ]
  },
  {
    path: '/integration-requests',
    component: () => import('@/components/AnalyticsLayout.vue'),
    children: [
      {
        path: '',
        name: 'integration-requests',
        component: () => import('@/components/integration-requests/IntegrationRequestsPage.vue')
      }
    ]
  },
  // Support routes (catch-all, must come last)
  {
    path: '/',
    name: 'root',
    component: () => import('@/components/Support.vue'),
    children: [
      {
        name: 'global',
        path: '/organization/:organizationId/global',
        component: () => import('@/components/OrganizationSettings.vue')
      },
      {
        name: 'location',
        path: '/organization/:organizationId/location/:locationId',
        component: () => import('@/components/LocationSettings.vue')
      },
      {
        name: 'settings',
        path: '/settings',
        component: () => import('@/components/Settings.vue')
      },
      {
        name: 'errors',
        path: '/settings/errors',
        component: () => import('@/components/PosErrorManager.vue')
      },
      {
        name: 'deletedLocations',
        path: '/settings/deletedLocations',
        component: () => import('@/components/DeletedLocations.vue')
      },
      {
        name: 'appVersions',
        path: '/settings/versions',
        component: () => import('@/components/AppVersions.vue')
      },
      {
        name: 'integrators',
        path: '/settings/integrators',
        component: () => import('@/components/Integrators.vue')
      },
      {
        path: '/settings/feature-flags',
        component: RouterView,
        redirect: '/settings/feature-flags/',
        children: [
          {
            path: '',
            name: 'featureFlags',
            component: () => import('@/components/feature-flags/FeatureFlags.vue')
          },
          {
            name: 'featureFlagDetails',
            path: ':id',
            component: () => import('@/components/feature-flags/FeatureFlagDetails.vue')
          }
        ]
      }
    ]
  },
  {
    path: '/desk-widget',
    name: 'desk-widget',
    component: () => import('@/components/desk-widget/DeskWidget.vue')
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/components/Login.vue')
  },
  {
    path: '/mcp-auth',
    name: 'mcp-auth',
    component: () => import('@/components/McpAuth.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach((to, _from, next) => {
  const needsAuth = to.name !== 'login' && to.name !== 'mcp-auth'
  const auth = useAuthStore()
  const isAuthenticated = auth.isAuthenticated
  if (needsAuth && !isAuthenticated) {
    next({
      name: 'login',
      query: { redirect: to.fullPath }
    })
  } else {
    const matchedRecord = to.matched.find(
      record => record.meta.requiredGroup || record.meta.requiredGroups
    )
    const requiredGroup = matchedRecord?.meta.requiredGroup as string | undefined
    const requiredGroups = matchedRecord?.meta.requiredGroups as string[] | undefined
    const hasAccess = requiredGroups
      ? requiredGroups.some(group => auth.supportGroups?.includes(group))
      : !requiredGroup || auth.supportGroups?.includes(requiredGroup)
    if (!hasAccess) {
      next({ name: 'root' })
    } else {
      next()
    }
  }
})

export default router
